import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, limit, getDocs, doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { nextLevel } from '../adaptive.js';

export default function Quiz() {
  const [q, setQ] = useState<any>(null);
  const [level, setLevel] = useState(1);
  const [streak, setStreak] = useState(0);
  const [loading, setLoading] = useState(true);
  const [grade, setGrade] = useState('1');
  const [score, setScore] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(5);
  const [questionsAnswered, setQuestionsAnswered] = useState(0);
  
  const navigate = useNavigate();

  // Get user's grade when component mounts
  useEffect(() => {
    async function fetchUserGrade() {
      if (!auth.currentUser) {
        navigate('/login');
        return;
      }
      
      try {
        const userRef = doc(db, 'users', auth.currentUser.uid);
        const userSnap = await getDoc(userRef);
        
        if (userSnap.exists()) {
          setGrade(userSnap.data().grade || '1');
        }
        
        loadQuestion();
      } catch (error) {
        console.error('Error fetching user grade:', error);
        setLoading(false);
      }
    }
    
    fetchUserGrade();
  }, [navigate]);

  // Load a new question when level changes
  useEffect(() => {
    if (grade) {
      loadQuestion();
    }
  }, [level, grade]);

  async function loadQuestion() {
    setLoading(true);
    try {
      // Try to get a question from Firestore
      const questionCollection = `questions_g${grade}`;
      const qSnap = await getDocs(
        query(collection(db, questionCollection),
              where('level', '==', level), 
              limit(1))
      );
      
      if (qSnap.docs.length > 0) {
        setQ(qSnap.docs[0].data());
      } else {
        // Fallback question if none found in Firestore
        setQ({
          question: `Level ${level} math question: What is ${level} + ${level + 1}?`,
          choices: [`${level}`, `${level + 1}`, `${level + 2}`, `${2 * level + 1}`],
          answer: `${2 * level + 1}`
        });
      }
    } catch (error) {
      console.error('Error loading question:', error);
      // Use fallback question
      setQ({
        question: "What is 2 + 3?",
        choices: ["4", "5", "6", "7"],
        answer: "5"
      });
    } finally {
      setLoading(false);
    }
  }

  function handleAnswer(choice: string) {
    const isCorrect = choice === q.answer;
    
    // Update score and questions answered
    if (isCorrect) {
      setScore(score + 1);
    }
    setQuestionsAnswered(questionsAnswered + 1);
    
    // Update streak and level
    const newStreak = isCorrect ? streak + 1 : 0;
    setStreak(newStreak);
    setLevel(currentLevel => nextLevel(currentLevel, newStreak));
    
    // If all questions answered, go to result page
    if (questionsAnswered + 1 >= totalQuestions) {
      navigate('/result', { 
        state: { 
          score: isCorrect ? score + 1 : score, 
          total: totalQuestions 
        } 
      });
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-xl">Loading question...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center">
      <div className="max-w-md w-full p-6 bg-white rounded-lg shadow-lg">
        <div className="flex justify-between mb-4">
          <span className="font-bold">Question {questionsAnswered + 1}/{totalQuestions}</span>
          <span className="font-bold">Score: {score}</span>
        </div>
        
        <div className="mb-6">
          <h2 className="text-xl font-bold mb-4">{q.question}</h2>
          
          <div className="space-y-3">
            {q.choices.map((c: string) => (
              <button
                key={c}
                onClick={() => handleAnswer(c)}
                className="block w-full p-3 rounded text-left bg-blue-50 hover:bg-blue-100"
              >
                {c}
              </button>
            ))}
          </div>
        </div>
        
        <div className="mt-4 text-sm text-gray-600">
          Level: {level} | Streak: {streak}
        </div>
      </div>
    </div>
  );
} 